import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import ColorPicker from 'common/components/Elements/ColorPicker/ColorPicker';
import { Messages } from 'common/components/Elements/ExpandableMessages/ExpandableMessages';
import type { Color } from 'common/utilities/colors/colors.types';
import { generateRandomString } from 'common/utilities/core/string';
import type * as I from 'core/api/coreApiClient';
import type { SxStyles } from 'core/utilities/theme';
import theme from 'core/utilities/theme';
import type React from 'react';
import { useState } from 'react';

export interface ComponentProps {
  className?: SxStyles;
  checked?: boolean;
  label?: string;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMsg?: I.Message[];
  e2eId?: string;
  /** Reduce the padding around boxes */
  small?: boolean;
  /** Reduce the size of the checkbox element (extension of the small attribute) */
  xsmall?: boolean;
  fullWidth?: boolean;
  title?: string;
  ariaLabel?: string;
  color?: Color;
  helpInfo?: I.Denotation;
}

function InputCheckbox({
  label, errorMsg, onChange, disabled, checked,
  e2eId, className, small, xsmall, fullWidth, title, ariaLabel, color, helpInfo,
}: ComponentProps) {
  const [id] = useState(() => generateRandomString());

  return (
    <FormControl
      error={errorMsg && errorMsg.length > 0}
      component="fieldset"
      sx={className}
      disabled={disabled}
      style={{ marginLeft: '-10px' }}
      fullWidth={fullWidth}
      title={title}
    >
      <div style={{ display: 'inline-flex' }}>
        <Checkbox
          id={id}
          color="primary"
          checked={checked}
          onChange={(e) => onChange(e)}
          data-e2e-form-checkbox={e2eId}
          data-e2e-form-checkbox-checked={checked}
          style={small ? { padding: '2px' } : {}}
          disabled={disabled}
          inputProps={{
            'aria-label': ariaLabel,
          }}
          size={xsmall ? 'small' : undefined}
        />
        {label && (
          <FormLabel
            htmlFor={id}
            style={{
              alignSelf: 'center',
              fontSize: small ? theme.typography.body2.fontSize : theme.typography.body1.fontSize,
              cursor: disabled ? 'default' : 'pointer',
            }}
            focused={false}
          >
            {color && (
              <Box sx={{ display: 'inline-block', verticalAlign: 'middle', mt: -0.5 }}>
                <ColorPicker color={checked ? color : [255, 255, 255]} />
              </Box>
            )}
            {label}
          </FormLabel>
        )}
      </div>
      {((errorMsg && errorMsg.length > 0) || helpInfo) && (
        <FormHelperText error={errorMsg && errorMsg.length > 0} component="div">
          <Messages messages={errorMsg} denotation={helpInfo} />
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default InputCheckbox;
